/*  User / Category service helpers  */
import { gql } from '@apollo/client';
import client     from './ApolloClient';

/* ────────────────────────────────
   GraphQL documents
   ──────────────────────────────── */
export const CREATE_CATEGORY_MUTATION = gql`
  mutation CreateCategory($name: String!) {
    createCategory(data: { name: $name }) { id name }
  }
`;

/* ▼  `roleName` added, `roleId` dropped  */
export const REGISTER_USER_MUTATION = gql`
  mutation RegisterUser(
    $email:       String!
    $name:        String!
    $password:    String!
    $userStatus:  String!
    $phoneNumber: String!
    $roleId:      Int!         # ← id of the Role
    $roleType:    RoleTypes!   # enum value
  ) {
    registerUser(
      data: {
        email:       $email
        name:        $name
        password:    $password
        userStatus:  $userStatus
        phoneNumber: $phoneNumber
        roleType:    $roleType
        role:        { connect: { id: $roleId } }
      }
    ) {
      id email name userStatus roleType phoneNumber createdAt updatedAt
      role { id name }
    }
  }
`;

export const UPDATE_USER_MUTATION = gql`
  mutation UpdateUser($where: UserWhereUniqueInput!, $data: UserUpdateInput!) {
    updateUser(where: $where, data: $data) {
      id name email phoneNumber userStatus roleType updatedAt
      role { id name }
    }
  }
`;

export const APPROVE_USER_MUTATION = gql`
  mutation approveUser($userId: Float!) {
    approveUser(userId: $userId) { id }
  }
`;

export const DELETE_USER_MUTATION = gql`
  mutation deleteUser($userId: Float!) {
    deleteUser(userId: $userId) { id }
  }
`;

export const GET_USERS_QUERY = gql`
  query {
    listUsers {
      id name createdAt email phoneNumber roleType userStatus
    }
  }
`;

/* ────────────────────────────────
   2. Role map  (key → { name, type })
   ──────────────────────────────── */
// 2️⃣ map your dropdown label → { id, type }
export const ROLE_MAP = {
  ADMIN:  { id: 1, name: 'ADMIN',  type: 'ADMIN'  },
  EDITOR: { id: 3, name: 'EDITOR', type: 'EDITOR' },
  READER: { id: 4, name: 'READER', type: 'READER' }
}

/* 3. registerUser helper */
export async function registerUser(formValues) {
  console.debug('registerUser got formValues:', formValues)

  const token = localStorage.getItem('authToken')
  const {
    email       = '',
    name        = '',
    password    = '',
    userStatus  = '',
    phoneNumber = '',
    // instead of “role” or “roleLabel” we now expect the enum key here:
    roleType    = 'READER'
  } = formValues

  // figure out lookup key: prefer the enum field, fall back to old label
  const key = (roleType && roleType.toUpperCase())
    || (formValues.role && formValues.role.toUpperCase())

  const roleObj = ROLE_MAP[key]
  if (!roleObj) {
    throw new Error(`Unknown role key "${key}"`)
  }

  const { data } = await client.mutate({
    mutation: REGISTER_USER_MUTATION,
    variables: {
      email,
      name,
      password,
      userStatus,
      phoneNumber,
      roleId:   roleObj.id,   // connect by ID
      roleType: roleObj.type  // enum for Prisma field
    },
    context: {
      headers: { Authorization: `Bearer ${token}` }
    }
  })

  console.debug('Registered user:', data.registerUser)
  return data.registerUser
}

export async function updateUserService(id, values) {
  const token = localStorage.getItem('authToken');
  const {
    name,
    email,
    phoneNumber,
    userStatus,
    role = 'Reader'
  } = values;

  const roleObj = ROLE_MAP[role];
  if (!roleObj) throw new Error(`Unknown role "${role}".`);

  /* ▼ keep legacy fields the backend expects */
  const variables = {
    where: { id: Number(id) },
    data : {
      name       : { set: name },
      email      : { set: email },
      phoneNumber: { set: phoneNumber },
      userStatus : { set: userStatus },
      roleType   : { set: roleObj.type },
      roleName   : { set: roleObj.name }
    }
  };

  const { data } = await client.mutate({
    mutation : UPDATE_USER_MUTATION,
    variables,
    context  : { headers: { Authorization: `Bearer ${token}` } }
  });

  return data.updateUser;
}

export async function fetchUsersService() {
  const token = localStorage.getItem('authToken');
  const { data } = await client.query({
    query         : GET_USERS_QUERY,
    context       : { headers: { Authorization: `Bearer ${token}` } },
    fetchPolicy   : 'network-only',
    nextFetchPolicy: 'network-only'
  });
  return data.listUsers;
}

export async function createCategoryService(name) {
  const token = localStorage.getItem('authToken');
  const { data } = await client.mutate({
    mutation: CREATE_CATEGORY_MUTATION,
    variables: { name },
    context : { headers: { Authorization: `Bearer ${token}` } }
  });
  return data.createCategory;
}

export async function approveUserService(userId) {
  const token = localStorage.getItem('authToken');
  const { data } = await client.mutate({
    mutation: APPROVE_USER_MUTATION,
    variables: { userId: Number(userId) },
    context : { headers: { Authorization: `Bearer ${token}` } }
  });
  return data.approveUser;
}

export async function deleteUserService(userId) {
  const token = localStorage.getItem('authToken');
  const { data } = await client.mutate({
    mutation: DELETE_USER_MUTATION,
    variables: { userId: Number(userId) },
    context : { headers: { Authorization: `Bearer ${token}` } }
  });
  return data.deleteUser;
}
